import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  MobileDrawer,
  Popover,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import { PREDEFINED_COMMENTS_FOR_ACTION } from '../constant';

import { invertedMessages } from './messages';

import './CertsDecommissionAction.css';

function CertsDecommissionAction({
  isMobileXlargeWidth,
  // autoRevoke,
  commentStatus,
  handleClick,
  // isLoading,
  getTypeActEntRole,
  placement,
  trigger,
  isDecommissionPopoverOpen,
  setIsDecommissionPopoverOpen,
}) {
  const intl = useIntl();
  const [isCritical, setIsCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const closeButtonRef = useRef(null);

  const { predefinedComments } = useSelector(
    (state) => state?.certifications?.details?.core
  );

  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const DECOMMISSION_ACTION_DATA = {
    TITLE: l('Decommission'),
  };

  // Reset on close
  useEffect(() => {
    if (!isDecommissionPopoverOpen) {
      setIsCritical(false);
      setCommentValue('');
    }
  }, [isDecommissionPopoverOpen]);

  useEffect(() => {
    if (isCritical) {
      setIsCritical(false);
    }
  }, [commentValue]);

  const isCommentNotValid = Boolean(
    isCritical ||
      commentValue?.replace(/[<>]/g, '')?.trim()?.length < 3 ||
      /<|>/.test(commentValue)
  );

  const handleClose = () => setIsDecommissionPopoverOpen(false);

  const handleCommentSubmit = () => {
    if (isCommentNotValid) return setIsCritical(true);

    handleClose();

    return handleClick(commentValue);
  };

  const titleString = `${DECOMMISSION_ACTION_DATA.TITLE} ${l(
    getTypeActEntRole
  )}?`;

  const getMobileHeader = () => (
    <Box className='CertsDecommissionAction-headerWrapper'>
      <Box className='CertsActionPopover-title'>
        <Icon
          kind='HelpFilled'
          className='CertsDecommissionAction-headerIcon'
        />
        <Typography kind='label' desktop='h3-d' mobile='h1-m'>
          {titleString}
        </Typography>
      </Box>
      <ButtonIcon
        ref={closeButtonRef}
        size={!isMobileXlargeWidth ? 'small' : 'medium'}
        kind='ghost'
        tabIndex={-1}
        ariaLabel={l('Close')}
        icon={<Icon kind='Close' />}
        onClick={handleClose}
      />
    </Box>
  );

  const getMobileContent = () => (
    <CertsModalCommentBox
      defaultComments={
        predefinedComments[PREDEFINED_COMMENTS_FOR_ACTION.DECOMMISSION]
      }
      showAlert={isCritical}
      onChange={setCommentValue}
      className='CertsActionPopover-commentBox'
      label={
        messages['comments.ATTESTATIONS'] ||
        localize(intl, getMessageKey('comments.ATTESTATIONS'))
      }
      size='medium'
      allowEditPredefinedComments={
        predefinedComments?.allowEditPredefinedComments
      }
    />
  );

  const getMobileFooter = () => (
    <FooterActionBar size='large' className='CertsActionPopover-controls'>
      <Button type='button' kind='ghost' onClick={handleClose}>
        {messages.CERTS_REJECT_CANCEL_BTN ||
          localize(intl, getMessageKey('CERTS_REJECT_CANCEL_BTN'))}
      </Button>
      <Button
        type='button'
        kind={!isCommentNotValid ? 'filled' : 'outlined'}
        onClick={() => handleCommentSubmit()}
        className='CertsActionPopover-submtBtn'>
        {l('Decommission')}
      </Button>
    </FooterActionBar>
  );

  // Render one time use multiple places
  const mobileHeader = getMobileHeader();
  const mobileContent = getMobileContent();
  const mobileFooter = getMobileFooter();

  const withRequiredComments = () => (
    <Box className='CertsDecommissionAction'>
      {mobileHeader}
      {/* TODO: Don't delete this code, might need in the future */}
      {/* <Typography className='Certs-Reject-Popover-commentsDesc' variant='h3'>{DECOMMISSION_ACTION_DATA.DESC}</Typography>{check && (<div className='CertsRejectPopover-NoteWrapper'><b className='CertsRejectPopover-NoteText'>Important: </b>{DECOMMISSION_ACTION_DATA.NOTE}</div>)}<HLine /> */}
      {mobileContent}
      <Box size='large' className='CertsActionPopover-controls'>
        <Button type='button' kind='ghost' onClick={handleClose}>
          {messages.CERTS_REJECT_CANCEL_BTN ||
            localize(intl, getMessageKey('CERTS_REJECT_CANCEL_BTN'))}
        </Button>
        <Button
          type='button'
          kind={!isCommentNotValid ? 'filled' : 'outlined'}
          onClick={() => handleCommentSubmit()}
          className='CertsActionPopover-submtBtn'>
          {l('Decommission')}
        </Button>
      </Box>
    </Box>
  );

  const getDecommissionActionPopover = () =>
    commentStatus ? withRequiredComments(true) : withRequiredComments(false);
  // TODO: Don't delete this code, might need in the future
  // if (autoRevoke) {return commentStatus ? withRequiredComments(true) : withAutoRevoke(true);} return commentStatus ? withRequiredComments(false) : withAutoRevoke(false);

  return (
    <Box tag='div' className='CertsDecommissionAction-contextWrapper'>
      {isMobileXlargeWidth ? (
        <>
          {trigger}
          <MobileDrawer
            isOpen={isDecommissionPopoverOpen}
            setIsOpen={setIsDecommissionPopoverOpen}
            closeButtonRef={closeButtonRef}
            headerContent={mobileHeader}
            content={mobileContent}
            footerActionBar={mobileFooter}
            className='CertsDecommissionAction'
          />
        </>
      ) : (
        <Popover
          isPortalEnabled
          placement={placement}
          trigger={trigger}
          popoverContent={getDecommissionActionPopover()}
          isOpen={isDecommissionPopoverOpen}
          setIsOpen={setIsDecommissionPopoverOpen}
        />
      )}
    </Box>
  );
}

CertsDecommissionAction.propTypes = {
  // autoRevoke: PropTypes.bool.isRequired,
  commentStatus: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  getTypeActEntRole: PropTypes.string.isRequired,
  placement: PropTypes.string.isRequired,
  trigger: PropTypes.element.isRequired,
  // isLoading: PropTypes.bool,
  isDecommissionPopoverOpen: PropTypes.bool.isRequired,
  setIsDecommissionPopoverOpen: PropTypes.func.isRequired,
  isMobileXlargeWidth: PropTypes.bool.isRequired,
};

CertsDecommissionAction.defaultProps = {};

export default CertsDecommissionAction;
